import React, { useEffect } from "react";
import { useState } from "react";
import { Theme } from "@mui/material/styles";
import { ThemeProvider, StyledEngineProvider, createTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Button, Card, CardActions, CardContent, TextField, Typography } from "@mui/material";
import ArrowForward from "@mui/icons-material/ArrowForward";
import { BaseContextType } from "../provisioningMachine";
import { useTranslation } from "@raven/translation";
import { errorColor } from "@raven/shared/constants/colors";
import QrReader from "react-qr-reader";
import { QrCodeSvg } from "./QrCodeSvg";
import { Platform } from "../native";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

const useStyles = makeStyles((theme: Theme) => {
  const commonValidityTextStyles = {
    marginTop: "3px",
  };
  return {
    root: {
      position: "relative",
    },
    cardRoot: {
      display: "flex",
      justifyContent: "center",
      margin: 8,
      width: 615,
      padding: "8% 0 1% 0",
      backgroundColor: "rgba(0,0,0,0.3)",
      color: "white",
      borderRadius: 12,
      // keep size consistent when switching between content (auth vs qr code scanning)
      minHeight: 492,
    },
    cardHeader: {
      fontSize: "18px",
      fontWeight: 500,
      justifyContent: "center",
      textAlign: "center",
      width: 300,
      margin: "0 auto 28px auto",
      wordBreak: "break-word",
    },
    cardContent: {
      padding: 0,
      paddingBottom: "14px !important",
    },
    input: {
      margin: 8,
      width: "100%",
      color: "white",
      borderColor: "white",
    },
    inputBorderProps: {
      color: "white",
      borderColor: "white",
    },
    formControl: {
      padding: 0,
      margin: 8,
      width: 340,
    },
    buttonContainer: {
      position: "relative",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: 340,
      margin: "64px 16px 16px 16px",
      padding: 0,
    },
    button: {
      margin: 0,
      color: "white",
      borderColor: "white",
      padding: "8px 16px",
      minWidth: 100,
    },
    invalidText: {
      position: "absolute",
      left: 0,
      ...commonValidityTextStyles,
      color: errorColor,
    },
    textFieldLabel: {
      color: "white",
      // this will be applied when input focused (label color change)
      "&$textFieldLabelFocused": {
        color: "white",
      },
    },
    textFieldLabelFocused: {},
    textFieldRoot: {
      color: "white",
      borderColor: "white",
      // this will be applied when hovered (input text color change)
      "&:hover": {
        color: "white",
      },
      // this will applied when hovered (input border color change)
      "&:hover $textFieldNotchedOutline": {
        borderColor: "white",
      },
      // this will be applied when focused (input border color change)
      "&$textFieldFocused $textFieldNotchedOutline": {
        borderColor: "white",
      },
    },
    textFieldFocused: {},
    textFieldNotchedOutline: {
      borderColor: "white",
    },
    qrCodeSvg: {
      cursor: "pointer",
      position: "absolute",
      right: -110,
    },
    returnToLoginTextContainer: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: "-56px",
      cursor: "pointer",
      margin: "0 auto -24px auto",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    returnToLoginText: {
      color: "white",
      fontSize: "20px",
      fontWeight: 500,
    },
    arrowForwardSvg: {
      transform: "scale(-1, 1)",
      marginRight: "8px",
    },
  };
});

export interface InteractiveProvisioningFormProps {
  initialValues: BaseContextType;
  onValidate(usernameCandidate: string, passwordCandidate: string): Promise<void>;
  onSubmit(newValues: BaseContextType): void;
}

/* eslint-disable sonarjs/cognitive-complexity */
const InteractiveProvisioningForm = ({
  initialValues,
  onValidate,
  onSubmit,
}: InteractiveProvisioningFormProps): React.JSX.Element => {
  const { translate } = useTranslation("provisioning");

  const classes = useStyles();
  const [credentials, setCredentials] = useState(initialValues);
  const [hasInvalidCredentials, setHasInvalidCredentials] = useState(false);
  const [showQrCodeScanner, setShowQrCodeScanner] = useState(false);
  const canTestCredentials = credentials?.username && credentials?.password;
  const canScanQrCode = Platform.OS === "ios" || process.env.NODE_ENV !== "production";

  // TODO: Is there a way to ensure the key is part of the BaseContextType interface?
  const handleChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    // trim value if we're updating the username
    const value = event.target.value;
    if (key === "username") {
      setCredentials({ ...credentials, [key]: value.trim() });
    } else if (key === "serial") {
      setCredentials({ ...credentials, [key]: value.trim() === "" ? undefined : value });
    } else {
      setCredentials({ ...credentials, [key]: value });
    }
  };

  const handleTestCredentials = async () => {
    // Test that credentials work before allowing them to be
    // saved into the credential store
    try {
      if (credentials?.username && credentials?.password) {
        await onValidate(credentials.username, credentials.password);
        return true;
      }
    } catch {
      console.error("Wrong password?");
    }
    setHasInvalidCredentials(true);
    return false;
  };

  const handleAuthentication = async () => {
    const hasValidCredentials = await handleTestCredentials();
    if (hasValidCredentials) {
      onSubmit(credentials);
    } else {
      setHasInvalidCredentials(true);
    }
  };

  const handleQrCodeScan = async (data: any) => {
    try {
      if (data) {
        const { serial, username, password } = JSON.parse(data);
        await onValidate(username, password);
        onSubmit({ serial, username, password });
      }
    } catch (error) {
      console.warn(error);
      setHasInvalidCredentials(true);
      setShowQrCodeScanner(false);
    }
  };

  const handleQrCodeError = (err: any) => {
    console.error(err);
  };

  // listen for enter key press
  useEffect(() => {
    const handleEnterKey = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleAuthentication();
      }
    };
    window.addEventListener("keydown", handleEnterKey);
    return () => {
      window.removeEventListener("keydown", handleEnterKey);
    };
  });

  return (
    <div className={classes.root}>
      <Card className={classes.cardRoot} variant="outlined">
        <CardContent className={classes.cardContent}>
          {showQrCodeScanner && (
            <>
              <Typography className={classes.cardHeader}>
                {translate("scanPrompt", "Please hold your code infront of the camera to be scanned.")}
              </Typography>
              <QrReader
                facingMode={"user"}
                delay={100}
                onScan={handleQrCodeScan}
                onError={handleQrCodeError}
                style={{ width: "100%", marginBottom: "12px" }}
              />
            </>
          )}
          {!showQrCodeScanner && (
            <>
              <Typography data-testid="provisioning_title" className={classes.cardHeader}>
                {translate("title", "Interactive Provisioning")}
              </Typography>
              <form noValidate={true} autoComplete="off" className={classes.formControl}>
                <div>
                  <TextField
                    className={classes.input}
                    variant="outlined"
                    required={true}
                    disabled={false}
                    id="serial"
                    label={translate("serial", "Serial")}
                    value={credentials?.serial ?? ""}
                    onChange={handleChange("serial")}
                    inputProps={{
                      "data-testid": "serial",
                      className: classes.inputBorderProps,
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.textFieldLabel,
                        focused: classes.textFieldLabelFocused,
                      },
                    }}
                    InputProps={{
                      classes: {
                        root: classes.textFieldRoot,
                        focused: classes.textFieldFocused,
                        notchedOutline: classes.textFieldNotchedOutline,
                      },
                    }}
                  />
                </div>
                <div>
                  <TextField
                    className={classes.input}
                    variant="outlined"
                    required={true}
                    disabled={false}
                    id="username"
                    label={translate("username", "Username")}
                    value={credentials?.username ?? ""}
                    onChange={handleChange("username")}
                    inputProps={{ autoCapitalize: "off", autoCorrect: "off", "data-testid": "username" }}
                    InputLabelProps={{
                      classes: {
                        root: classes.textFieldLabel,
                        focused: classes.textFieldLabelFocused,
                      },
                    }}
                    InputProps={{
                      classes: {
                        root: classes.textFieldRoot,
                        focused: classes.textFieldFocused,
                        notchedOutline: classes.textFieldNotchedOutline,
                      },
                    }}
                  />
                </div>
                <div>
                  <TextField
                    className={classes.input}
                    variant="outlined"
                    required={true}
                    id="password"
                    label={translate("password", "Password")}
                    value={credentials?.password ?? ""}
                    onChange={handleChange("password")}
                    type="password"
                    inputProps={{ "data-testid": "password" }}
                    InputLabelProps={{
                      classes: {
                        root: classes.textFieldLabel,
                        focused: classes.textFieldLabelFocused,
                      },
                    }}
                    InputProps={{
                      classes: {
                        root: classes.textFieldRoot,
                        focused: classes.textFieldFocused,
                        notchedOutline: classes.textFieldNotchedOutline,
                      },
                    }}
                  />
                </div>
              </form>
              <CardActions className={classes.buttonContainer}>
                <Button
                  data-testid="continueBtn"
                  variant="outlined"
                  className={classes.button}
                  disabled={!canTestCredentials}
                  onClick={handleAuthentication}
                >
                  {translate("continue", "Continue")}
                </Button>
                {hasInvalidCredentials && (
                  <Typography className={classes.invalidText}>
                    {translate("invalidCredentials", "INVALID")}
                  </Typography>
                )}
                {canScanQrCode && (
                  <QrCodeSvg className={classes.qrCodeSvg} onClick={() => setShowQrCodeScanner(true)} />
                )}
              </CardActions>
            </>
          )}
        </CardContent>
      </Card>
      {showQrCodeScanner && (
        <div className={classes.returnToLoginTextContainer}>
          <ArrowForward className={classes.arrowForwardSvg} />
          <Typography onClick={() => setShowQrCodeScanner(false)} className={classes.returnToLoginText}>
            {translate("returnToLogin", "Return to Login")}
          </Typography>
        </div>
      )}
    </div>
  );
};

const theme = createTheme();
const InteractiveProvisioningFormWithThemeProvider = (
  props: InteractiveProvisioningFormProps
): React.JSX.Element => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <InteractiveProvisioningForm {...props} />
    </ThemeProvider>
  </StyledEngineProvider>
);

export default InteractiveProvisioningFormWithThemeProvider;
